import { useCallback, useEffect, useState } from 'react';
import axios from '../api/axios';
import { NUMERATION_SCHEMA_ENDPOINT, TENANT_SETTINGS_ENDPOINT } from '../consts';
import { TenantSettings } from '../types/tenantSettings';

const SETTINGS_DETAIL_URL = `${TENANT_SETTINGS_ENDPOINT}settings/`;

/**
 * The tenant settings singleton, refetched whenever any form on the workspace
 * dispatches `tenantSettingsUpdated`. Several settings pages read the same
 * payload, so they share this hook rather than each rolling its own fetch.
 */
export const useTenantSettings = () => {
  const [settings, setSettings] = useState<TenantSettings | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(SETTINGS_DETAIL_URL);
      setSettings(data);
    } catch {
      setSettings(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    window.addEventListener('tenantSettingsUpdated', refresh);
    return () => {
      window.removeEventListener('tenantSettingsUpdated', refresh);
    };
  }, [refresh]);

  return { settings, loading, refresh };
};

/**
 * Numeration schema names by id, so a stored schema id can be shown as the
 * name staff picked it by. Resolves to an empty map when the viewer cannot
 * list schemas.
 */
export const useNumerationSchemaNames = (): Record<string, string> => {
  const [names, setNames] = useState<Record<string, string>>({});

  useEffect(() => {
    let isCurrent = true;

    const load = async () => {
      try {
        const { data } = await axios.get(NUMERATION_SCHEMA_ENDPOINT);
        const schemas: { id: string | number; name: string }[] = Array.isArray(data?.results)
          ? data.results
          : data;
        if (isCurrent && Array.isArray(schemas)) {
          setNames(Object.fromEntries(schemas.map((schema) => [String(schema.id), schema.name])));
        }
      } catch {
        /* The names are a nicety; the raw id is never shown instead. */
      }
    };

    load();
    window.addEventListener('numerationSchemasUpdated', load);
    return () => {
      isCurrent = false;
      window.removeEventListener('numerationSchemasUpdated', load);
    };
  }, []);

  return names;
};
